import { useState } from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useConfirmPaymentOrderMutation,
  useGetPaymentOrderByIdLazyQuery,
  useGetWalletDetailsV2Query,
  useRejectPaymentOrderMutation,
} from '@/types/generated/graphql';
import { useModal } from '@shared/hooks/use-modal';
import { usePaymentMethodsList } from '@modules/settings/subscription/hooks/use-payment-methods-list';
import { useLoadWallet } from '@modules/settings/my-numbers/purchase-number/hooks/use-load-wallet';
import { centsToDollar } from '@modules/settings/subscription/utils/utils';
import { getIsAutoRechargeEnabled } from '../confirm-payment.helpers';

type Step = 'review' | 'confirm' | 'cancel-request' | null;

export function useConfirmPayment() {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(null);
  const [paymentOrderId, setPaymentOrderId] = useState<number | null>(null);

  const payModal = useModal();
  const paymentMethodsList = usePaymentMethodsList();
  const loadWallet = useLoadWallet();

  const [fetchPaymentOrder, paymentOrderResult] = useGetPaymentOrderByIdLazyQuery();
  const walletResult = useGetWalletDetailsV2Query({ fetchPolicy: 'network-only', skip: step !== 'confirm' });

  const handleClose = () => {
    setStep(null);
    setPaymentOrderId(null);
  };

  const [confirmPaymentOrder, { loading: confirming }] = useConfirmPaymentOrderMutation({
    refetchQueries: ['ListPaymentOrders'],
    onCompleted: (data) => {
      notification.success({
        message: data?.confirmPaymentOrder?.message ?? t('payment_confirmed_successfully'),
      });
      handleClose();
    },
    onError: (error) => notification.error({ message: error.message }),
  });

  const openReview = (id: number) => {
    setPaymentOrderId(id);
    setStep('review');
    fetchPaymentOrder({ variables: { paymentOrderId: id } });
  };

  const goToConfirmPayment = () => setStep('confirm');
  const backToReview = () => setStep('review');

  const openCancelRequest = () => setStep('cancel-request');
  const keepRequest = () => setStep('review');

  const [rejectPaymentOrder, { loading: rejecting }] = useRejectPaymentOrderMutation({
    refetchQueries: ['ListPaymentOrders'],
    onCompleted: (data) => {
      notification.success({
        message: data?.rejectPaymentOrder?.message ?? t('payment_request_cancelled_successfully'),
      });
      handleClose();
    },
    onError: (error) => notification.error({ message: error.message }),
  });

  const cancelPaymentRequest = () => {
    if (!paymentOrderId) return;
    rejectPaymentOrder({ variables: { input: { paymentOrderId } } });
  };

  const paymentOrder = paymentOrderResult.data?.getPaymentOrderById?.data;
  const walletBalance = centsToDollar(walletResult.data?.getWalletDetailsV2?.data?.amount ?? 0);
  const totalAmount = centsToDollar(paymentOrder?.chargedAmount ?? paymentOrder?.cartAmount ?? 0);
  const isInsufficientBalance = walletBalance < totalAmount;
  const isAutoRechargeEnabled = getIsAutoRechargeEnabled(walletResult.data);

  const getTooltipMessage = (): string | null => {
    if (isAutoRechargeEnabled && !isInsufficientBalance) {
      return t('this_action_triggers_your_auto_recharge');
    }
    if (isAutoRechargeEnabled && isInsufficientBalance) {
      return t('auto_recharge_insufficient_wallet_message');
    }
    return t('auto_recharge_disabled');
  };

  const handleConfirmPayment = () => {
    if (!paymentOrderId) return;
    confirmPaymentOrder({ variables: { paymentOrderId } });
  };

  const handleLoadWallet = () => payModal.handleOpen();
  const handleWalletLoaded = () => walletResult.refetch();

  return {
    step,
    paymentOrder,
    paymentOrderResult,
    openReview,
    handleClose,
    goToConfirmPayment,
    backToReview,
    openCancelRequest,
    keepRequest,
    cancelPaymentRequest,
    rejecting,
    walletResult,
    walletBalance,
    totalAmount,
    isInsufficientBalance,
    isAutoRechargeEnabled,
    getTooltipMessage,
    handleConfirmPayment,
    handleLoadWallet,
    handleWalletLoaded,
    confirming,
    payModal,
    paymentMethodsList,
    loadWallet,
  };
}
